import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Trophy } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import SectionCard from '@/components/dashboard/widgets/SectionCard';
import { buildVendorProfileFromAverages } from '@/utils/vendorProfileBuilder';
import { getScoreColor } from '@/types/vendor-kpi';
import { toScore99 } from '@/utils/scoreFormat';
import type { TeamResponse } from '@/services/teamService';
import type { SalespersonStats, TeamRankingItem } from '@/types/session';
import type { VendorKpiCategory } from '@/types/vendor-kpi';

type Props = {
  teams: TeamResponse[];
  statsByUserId?: Record<string, SalespersonStats>;
  teamRanking: TeamRankingItem[];
  loading: boolean;
};

function initials(first?: string, last?: string): string {
  return ((first?.[0] ?? '') + (last?.[0] ?? '')).toUpperCase() || '?';
}

function topKpis(kpiJson?: string, n = 3): VendorKpiCategory[] {
  if (!kpiJson) return [];
  const profile = buildVendorProfileFromAverages(kpiJson);
  if (!profile) return [];
  return [...profile.categories].sort((a, b) => b.score - a.score).slice(0, n);
}

export default function TeamMembersCard({ teams, statsByUserId = {}, teamRanking, loading }: Props) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'members' | 'ranking'>('members');
  const [selectedId, setSelectedId] = useState<string>(() => teams[0]?.id ?? '');

  const selected = useMemo(
    () => teams.find((team) => team.id === selectedId) ?? teams[0],
    [teams, selectedId]
  );

  return (
    <SectionCard
      title={t('dashboard.manager.teamsWidget', 'Equipos')}
      action={
        tab === 'members' && teams.length > 1 ? (
          <Select value={selected?.id ?? ''} onValueChange={setSelectedId}>
            <SelectTrigger size="sm" className="h-9 min-w-[180px] text-sm">
              <SelectValue placeholder={t('dashboard.manager.selectTeam', 'Equipo')} />
            </SelectTrigger>
            <SelectContent>
              {teams.map((team) => (
                <SelectItem key={team.id} value={team.id}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null
      }
      contentClassName="space-y-0"
    >
      <Tabs value={tab} onValueChange={(v) => setTab(v as 'members' | 'ranking')} className="gap-4">
        <TabsList className="w-full max-w-sm">
          <TabsTrigger value="members" className="gap-1.5">
            <Users className="size-4" />
            {t('dashboard.manager.members', 'Miembros')}
          </TabsTrigger>
          <TabsTrigger value="ranking" className="gap-1.5">
            <Trophy className="size-4" />
            {t('dashboard.manager.ranking', 'Ranking equipos')}
          </TabsTrigger>
        </TabsList>

        {/* MEMBERS TAB */}
        <TabsContent value="members" className="mt-0">
          {loading ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Skeleton className="h-36" />
              <Skeleton className="h-36" />
              <Skeleton className="h-36" />
              <Skeleton className="h-36" />
            </div>
          ) : !selected ? (
            <EmptyBlock icon={Users} message={t('dashboard.manager.noTeams', 'Sin equipos todavía')} />
          ) : selected.members.length === 0 ? (
            <EmptyBlock icon={Users} message={t('dashboard.manager.noMembers', 'Sin miembros todavía')} />
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-muted-foreground/70">
                  {selected.members.length} {t('dashboard.manager.membersShort', 'miembros')}
                </p>
                <Button asChild variant="ghost" size="sm">
                  <Link to="/team">
                    {t('common.viewAll', 'Ver todos')}
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {selected.members.slice(0, 6).map((m) => {
                  const stats = statsByUserId[m.id];
                  const profile = stats?.kpiAveragesJson
                    ? buildVendorProfileFromAverages(stats.kpiAveragesJson)
                    : null;
                  const globalScore = profile?.globalScore ?? 0;
                  const top3 = topKpis(stats?.kpiAveragesJson, 3);

                  return (
                    <Card key={m.id} className="overflow-hidden py-0 hover:border-primary/40 transition-colors">
                      <CardContent className="p-4">
                        {/* Header: avatar + name + score */}
                        <div className="flex items-center gap-3 mb-3">
                          <Avatar className="size-10">
                            <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                              {initials(m.firstName, m.lastName)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-semibold text-foreground">
                              {m.firstName} {m.lastName}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {stats?.sessionCount ?? 0} {t('dashboard.manager.sessionsShort', 'sesiones')}
                            </div>
                          </div>
                          <div className="shrink-0 text-2xl font-bold tabular-nums" style={{ color: getScoreColor(globalScore) }}>
                            {globalScore || '—'}
                          </div>
                        </div>

                        {/* Top 3 KPI mini-bars */}
                        {top3.length > 0 && (
                          <div className="space-y-1.5">
                            {top3.map((cat) => (
                              <div key={cat.id} className="flex items-center gap-2">
                                <span className="w-8 text-[10px] font-bold text-muted-foreground">{cat.shortName}</span>
                                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-border">
                                  <div
                                    className="h-full rounded-full transition-all"
                                    style={{
                                      width: `${(cat.score / 99) * 100}%`,
                                      backgroundColor: getScoreColor(cat.score),
                                    }}
                                  />
                                </div>
                                <span className="w-6 text-right text-[10px] font-semibold tabular-nums" style={{ color: getScoreColor(cat.score) }}>
                                  {cat.score}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </TabsContent>

        {/* RANKING TEAMS TAB */}
        <TabsContent value="ranking" className="mt-0">
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : teamRanking.length === 0 ? (
            <EmptyBlock icon={Trophy} message={t('dashboard.manager.noRanking', 'Sin datos de ranking')} />
          ) : (
            <div className="space-y-2">
              {teamRanking.slice(0, 6).map((team, i) => (
                <div
                  key={team.teamId}
                  className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-3 hover:border-primary/40 transition-colors"
                >
                  <span className="w-6 text-center text-sm font-semibold tabular-nums text-muted-foreground">
                    #{i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">{team.teamName}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {team.memberCount} {t('dashboard.manager.membersShort', 'miembros')} ·{' '}
                      {team.totalSessions} {t('dashboard.manager.sessionsShort', 'sesiones')}
                    </div>
                  </div>
                  <div className="shrink-0 text-xl font-semibold tabular-nums text-primary">
                    {toScore99(team.averageScore)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </SectionCard>
  );
}

function EmptyBlock({ icon: Icon, message }: { icon: React.ComponentType<{ className?: string }>; message: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
      <Icon className="size-6 text-muted-foreground/50" />
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}
